import {
  getLocalLiveEventPhase,
  getLocalLiveStopCoordinates,
} from "./localLive";

export const FALL_CRAWL_PASSPORT_KEY = "fall-crawl-2026";
export const FALL_CRAWL_PASSPORT_SCHEMA_VERSION = 1;
export const FALL_CRAWL_PASSPORT_STORAGE_PREFIX = "mfp:fall-crawl-2026-passport";
export const FALL_CRAWL_PASSPORT_CLAIM_RADIUS_METERS = 125;
export const FALL_CRAWL_GIVEAWAY_STAMP_THRESHOLD = 5;
export const FALL_CRAWL_PASSPORT_COUNTIES = Object.freeze({
  bedford: {
    key: "bedford",
    label: "Bedford County",
  },
  coffee: {
    key: "coffee",
    label: "Coffee County",
  },
});

export const FALL_CRAWL_PASSPORT_DEFAULT_STOP_CONFIG = Object.freeze([
  {
    stopId: "historic-town-of-normandy-farmers-market",
    displayName: "Historic Town of Normandy Farmers Market",
    countyKey: "bedford",
    stampAssetKey: "normandy",
    requiredForCountyCompletion: true,
    requiredForRouteCompletion: true,
  },
]);

const FALL_CRAWL_BEDFORD_TOWNS = new Set([
  "bell buckle",
  "normandy",
  "shelbyville",
  "unionville",
  "wartrace",
]);

const FALL_CRAWL_COFFEE_TOWNS = new Set([
  "beechgrove",
  "hillsboro",
  "manchester",
  "summitville",
  "tullahoma",
]);

const FALL_CRAWL_PASSPORT_EXCLUDED_STOP_LOOKUPS = new Set([
  "mfp test",
  "myfarmingpal test",
  "my farming pal test",
]);

const FALL_CRAWL_PASSPORT_OFFICIAL_STOP_COUNTY_BY_LOOKUP = {
  "flour power": "bedford",
  "native fare farms": "coffee",
};

const normalizeLookupText = (value = "") =>
  String(value)
    .toLowerCase()
    .replace(/&/g, " and ")
    .replace(/[^a-z0-9]+/g, " ")
    .trim();

const safeNowIso = () => new Date().toISOString();

const getStorageKey = (eventId, options = {}) => {
  const suffix = options.storageKeySuffix ? `:${String(options.storageKeySuffix)}` : "";
  return `${FALL_CRAWL_PASSPORT_STORAGE_PREFIX}:${eventId || "unknown-event"}${suffix}`;
};

const asArray = (value) => Array.isArray(value) ? value : [];

export const createAnonymousFallCrawlPassportId = () => {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return `passport-${crypto.randomUUID()}`;
  }
  if (typeof crypto !== "undefined" && typeof crypto.getRandomValues === "function") {
    const values = crypto.getRandomValues(new Uint32Array(4));
    return `passport-${Array.from(values).map((value) => value.toString(36)).join("-")}`;
  }
  return `passport-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
};

export const isValidFallCrawlPassportId = (value = "") =>
  /^passport-[A-Za-z0-9_-]{12,80}$/.test(String(value || "").trim());

const normalizeCollectedStops = (collectedStops = {}) => {
  if (Array.isArray(collectedStops)) {
    return collectedStops.reduce((items, stopId) => {
      if (stopId) items[String(stopId)] = safeNowIso();
      return items;
    }, {});
  }
  return Object.entries(collectedStops || {}).reduce((items, [stopId, timestamp]) => {
    if (stopId) items[String(stopId)] = timestamp || safeNowIso();
    return items;
  }, {});
};

export const createEmptyFallCrawlPassportState = (eventId = "") => ({
  version: FALL_CRAWL_PASSPORT_SCHEMA_VERSION,
  eventKey: FALL_CRAWL_PASSPORT_KEY,
  eventId,
  anonymousPassportId: createAnonymousFallCrawlPassportId(),
  collectedStops: {},
  verifiedStampCount: 0,
  giveawayPromptDismissed: false,
  giveawayEntrySubmitted: false,
  giveawayDeclined: false,
  unlockedCountyBadges: [],
  routeComplete: false,
  updatedAt: safeNowIso(),
});

export const normalizeFallCrawlPassportState = (state = {}, eventId = "") => {
  const next = {
    ...createEmptyFallCrawlPassportState(eventId),
    ...state,
    version: FALL_CRAWL_PASSPORT_SCHEMA_VERSION,
    eventKey: FALL_CRAWL_PASSPORT_KEY,
    eventId: state.eventId || eventId,
    anonymousPassportId: isValidFallCrawlPassportId(state.anonymousPassportId)
      ? String(state.anonymousPassportId)
      : createAnonymousFallCrawlPassportId(),
    collectedStops: normalizeCollectedStops(state.collectedStops),
    verifiedStampCount: Math.max(0, Math.floor(Number(state.verifiedStampCount || 0))),
    giveawayPromptDismissed: state.giveawayPromptDismissed === true,
    giveawayEntrySubmitted: state.giveawayEntrySubmitted === true,
    giveawayDeclined: state.giveawayDeclined === true,
    unlockedCountyBadges: [...new Set(asArray(state.unlockedCountyBadges).filter(Boolean))],
    routeComplete: state.routeComplete === true,
    updatedAt: state.updatedAt || safeNowIso(),
  };
  return next;
};

export const loadFallCrawlPassportState = (eventId, storage = window.localStorage, options = {}) => {
  if (!storage) return createEmptyFallCrawlPassportState(eventId);
  try {
    const raw = storage.getItem(getStorageKey(eventId, options));
    return normalizeFallCrawlPassportState(raw ? JSON.parse(raw) : {}, eventId);
  } catch {
    return createEmptyFallCrawlPassportState(eventId);
  }
};

export const saveFallCrawlPassportState = (eventId, state, storage = window.localStorage, options = {}) => {
  const next = normalizeFallCrawlPassportState(
    { ...state, updatedAt: safeNowIso() },
    eventId
  );
  if (storage) storage.setItem(getStorageKey(eventId, options), JSON.stringify(next));
  return next;
};

export const resetFallCrawlPassportState = (eventId, storage = window.localStorage, options = {}) => {
  const next = createEmptyFallCrawlPassportState(eventId);
  if (storage) storage.setItem(getStorageKey(eventId, options), JSON.stringify(next));
  return next;
};

export const isFallCrawlPassportIdentity = (event = {}) =>
  event.passportEventKey === FALL_CRAWL_PASSPORT_KEY ||
  event.slug === FALL_CRAWL_PASSPORT_KEY ||
  event.fallCrawl2026 === true;

export const isFallCrawlPassportEvent = (event = {}) =>
  isFallCrawlPassportIdentity(event) &&
  (
    event.passportEnabled === true ||
    event.passportCollectionEnabled === true ||
    event.fallCrawlPublicReveal?.passportCollectionEnabled === true
  );

const normalizeStopConfig = (config = {}) => {
  const countyKey = config.countyKey || "";
  if (!config.stopId || !FALL_CRAWL_PASSPORT_COUNTIES[countyKey]) return null;
  return {
    stopId: String(config.stopId),
    displayName: config.displayName || "",
    passportEligible: true,
    countyKey,
    stampAssetKey: config.stampAssetKey || "generic",
    requiredForCountyCompletion: config.requiredForCountyCompletion !== false,
    requiredForRouteCompletion: config.requiredForRouteCompletion !== false,
  };
};

const getEventStopConfig = (event = {}) =>
  asArray(event.passportStopConfig)
    .map(normalizeStopConfig)
    .filter(Boolean);

const getStopFieldConfig = (stop = {}) =>
  stop.passportEligible === true
    ? normalizeStopConfig({
      stopId: stop.id,
      displayName: stop.passportDisplayName || stop.name,
      countyKey: stop.passportCountyKey,
      stampAssetKey: stop.passportStampKey,
      requiredForCountyCompletion: stop.passportRequiredForCountyCompletion,
      requiredForRouteCompletion: stop.passportRequiredForRouteCompletion,
    })
    : null;

const getStopSearchText = (stop = {}) =>
  normalizeLookupText([
    stop.name,
    stop.displayName,
    stop.title,
    stop.city,
    stop.county,
    stop.address,
    stop.address1,
    stop.locationName,
    stop.publicLocationLabel,
  ].filter(Boolean).join(" "));

const getFallCrawlStopNameLookups = (stop = {}) =>
  [
    stop.passportDisplayName,
    stop.name,
    stop.displayName,
    stop.title,
  ].map(normalizeLookupText).filter(Boolean);

const isFallCrawlExcludedPassportStop = (stop = {}) =>
  getFallCrawlStopNameLookups(stop)
    .some((value) => FALL_CRAWL_PASSPORT_EXCLUDED_STOP_LOOKUPS.has(value));

const getFallCrawlOfficialCountyOverrideForStop = (stop = {}) =>
  getFallCrawlStopNameLookups(stop)
    .map((value) => FALL_CRAWL_PASSPORT_OFFICIAL_STOP_COUNTY_BY_LOOKUP[value])
    .find(Boolean) || "";

const getFallCrawlCountyKeyForStop = (stop = {}) => {
  const explicitCounty = normalizeLookupText(stop.passportCountyKey || stop.countyKey || stop.county);
  if (explicitCounty.includes("bedford")) return "bedford";
  if (explicitCounty.includes("coffee")) return "coffee";

  const city = normalizeLookupText(stop.city);
  if (FALL_CRAWL_BEDFORD_TOWNS.has(city)) return "bedford";
  if (FALL_CRAWL_COFFEE_TOWNS.has(city)) return "coffee";

  const searchText = getStopSearchText(stop);
  if ([...FALL_CRAWL_BEDFORD_TOWNS].some((town) => searchText.includes(town))) return "bedford";
  if ([...FALL_CRAWL_COFFEE_TOWNS].some((town) => searchText.includes(town))) return "coffee";

  return "";
};

const getFallCrawlStampAssetKeyForStop = (stop = {}) => {
  const explicitStamp = normalizeLookupText(stop.passportStampKey || stop.stampAssetKey);
  if (explicitStamp.includes("normandy")) return "normandy";

  const searchText = getStopSearchText(stop);
  return searchText.includes("normandy farmers market") ||
    (searchText.includes("historic town") && searchText.includes("normandy"))
    ? "normandy"
    : "generic";
};

const getFallCrawlDerivedStopConfig = (stop = {}) => {
  const officialCountyOverride = getFallCrawlOfficialCountyOverrideForStop(stop);
  if (!stop?.id || (stop.passportEligible === false && !officialCountyOverride)) return null;
  const countyKey = officialCountyOverride || getFallCrawlCountyKeyForStop(stop);
  if (!FALL_CRAWL_PASSPORT_COUNTIES[countyKey]) return null;
  return normalizeStopConfig({
    stopId: stop.id,
    displayName: stop.passportDisplayName || stop.name || stop.displayName || stop.title || "",
    countyKey,
    stampAssetKey: getFallCrawlStampAssetKeyForStop(stop),
    requiredForCountyCompletion: stop.passportRequiredForCountyCompletion,
    requiredForRouteCompletion: stop.passportRequiredForRouteCompletion,
  });
};

export const getFallCrawlPassportStops = (event = {}, stops = [], options = {}) => {
  const allowDisabledPreview = options.allowDisabledPreview === true;
  const canBuildStops = allowDisabledPreview
    ? isFallCrawlPassportIdentity(event)
    : isFallCrawlPassportEvent(event);
  if (!canBuildStops) return [];

  const eventStopConfig = getEventStopConfig(event);
  const configuredByStopId = new Map();
  [...FALL_CRAWL_PASSPORT_DEFAULT_STOP_CONFIG, ...eventStopConfig]
    .map(normalizeStopConfig)
    .filter(Boolean)
    .forEach((config) => configuredByStopId.set(config.stopId, config));

  const stopItems = stops
    .map((stop) => {
      if (isFallCrawlExcludedPassportStop(stop)) return null;
      const config = configuredByStopId.get(stop.id) ||
        getStopFieldConfig(stop) ||
        getFallCrawlDerivedStopConfig(stop);
      if (!config) return null;
      return {
        ...config,
        stop,
        stopId: stop.id,
        displayName: config.displayName || stop.name || "Fall Crawl stop",
        town: [stop.city, stop.state].filter(Boolean).join(", "),
        order: Number(stop.order || 0),
        coordinates: getLocalLiveStopCoordinates(stop),
      };
    })
    .filter(Boolean);

  return stopItems.sort((a, b) => a.order - b.order);
};

export const getFallCrawlPassportProgress = (passportStops = [], state = {}) => {
  const collected = normalizeCollectedStops(state.collectedStops);
  const collectedIds = new Set(Object.keys(collected));
  const collectedCount = passportStops.filter((item) => collectedIds.has(item.stopId)).length;
  const totalStops = passportStops.length;
  const percent = totalStops > 0 ? Math.round((collectedCount / totalStops) * 100) : 0;
  return {
    collectedCount,
    totalStops,
    percent,
    label: `${collectedCount} of ${totalStops} ${totalStops === 1 ? "stop" : "stops"} visited`,
  };
};

export const getFallCrawlGiveawayStatus = (state = {}) => {
  const current = normalizeFallCrawlPassportState(state, state.eventId);
  const verifiedStampCount = current.verifiedStampCount;
  return {
    threshold: FALL_CRAWL_GIVEAWAY_STAMP_THRESHOLD,
    verifiedStampCount,
    eligible: verifiedStampCount >= FALL_CRAWL_GIVEAWAY_STAMP_THRESHOLD,
    promptDismissed: current.giveawayPromptDismissed === true,
    entrySubmitted: current.giveawayEntrySubmitted === true,
    declined: current.giveawayDeclined === true,
  };
};

export const normalizeFallCrawlGiveawayEmail = (value = "") => {
  const email = String(value || "").trim().toLowerCase().slice(0, 254);
  if (!email) return "";
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email) ? email : "";
};

export const normalizeFallCrawlGiveawayPhone = (value = "") => {
  const raw = String(value || "").trim();
  const digits = raw.replace(/\D/g, "");
  if (digits.length < 7 || digits.length > 15) return "";
  return digits.length === 10 ? `+1${digits}` : `+${digits}`;
};

export const getFallCrawlCountyProgress = (passportStops = [], state = {}) => {
  const collected = normalizeCollectedStops(state.collectedStops);
  return Object.values(FALL_CRAWL_PASSPORT_COUNTIES).map((county) => {
    const requiredStops = passportStops.filter(
      (item) => item.countyKey === county.key && item.requiredForCountyCompletion !== false
    );
    const collectedCount = requiredStops.filter((item) => collected[item.stopId]).length;
    const complete = requiredStops.length > 0 && collectedCount === requiredStops.length;
    return {
      ...county,
      collectedCount,
      totalStops: requiredStops.length,
      complete,
    };
  });
};

export const getFallCrawlRouteComplete = (passportStops = [], state = {}) => {
  const collected = normalizeCollectedStops(state.collectedStops);
  const requiredStops = passportStops.filter((item) => item.requiredForRouteCompletion !== false);
  return requiredStops.length > 0 && requiredStops.every((item) => collected[item.stopId]);
};

export const refreshFallCrawlPassportUnlocks = (passportStops = [], state = {}) => {
  const countyProgress = getFallCrawlCountyProgress(passportStops, state);
  const unlockedCountyBadges = countyProgress
    .filter((county) => county.complete)
    .map((county) => county.key);
  return {
    ...normalizeFallCrawlPassportState(state, state.eventId),
    unlockedCountyBadges,
    routeComplete: getFallCrawlRouteComplete(passportStops, state),
  };
};

export const collectFallCrawlPassportStop = (passportStops = [], state = {}, stopId = "") => {
  const target = passportStops.find((item) => item.stopId === stopId);
  if (!target) {
    return {
      state: normalizeFallCrawlPassportState(state, state.eventId),
      awarded: false,
      reason: "not-eligible",
    };
  }

  const current = normalizeFallCrawlPassportState(state, state.eventId);
  if (current.collectedStops[stopId]) {
    return { state: current, awarded: false, reason: "duplicate", stop: target };
  }

  const collectedStops = {
    ...current.collectedStops,
    [stopId]: safeNowIso(),
  };
  const next = refreshFallCrawlPassportUnlocks(passportStops, {
    ...current,
    collectedStops,
  });
  return { state: next, awarded: true, reason: "collected", stop: target };
};

export const removeFallCrawlPassportStop = (passportStops = [], state = {}, stopId = "") => {
  const current = normalizeFallCrawlPassportState(state, state.eventId);
  const { [stopId]: removed, ...remainingStops } = current.collectedStops;
  return refreshFallCrawlPassportUnlocks(passportStops, {
    ...current,
    collectedStops: remainingStops,
  });
};

export const canCollectFallCrawlPassportStamps = (event = {}, { isAdminTest = false, now = new Date() } = {}) => {
  if (!isFallCrawlPassportEvent(event)) return false;
  if (isAdminTest) return true;
  return getLocalLiveEventPhase(event, now) === "live";
};

export const getDistanceMeters = (from, to) => {
  if (!from || !to) return Infinity;
  const toRadians = (value) => value * (Math.PI / 180);
  const earthRadiusMeters = 6371000;
  const dLat = toRadians(to.lat - from.lat);
  const dLng = toRadians(to.lng - from.lng);
  const lat1 = toRadians(from.lat);
  const lat2 = toRadians(to.lat);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.sin(dLng / 2) ** 2 * Math.cos(lat1) * Math.cos(lat2);
  return earthRadiusMeters * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
};

export const isWithinFallCrawlPassportRadius = (
  visitorLocation,
  stopLocation,
  radiusMeters = FALL_CRAWL_PASSPORT_CLAIM_RADIUS_METERS
) => getDistanceMeters(visitorLocation, stopLocation) <= radiusMeters;
